<template>
	<div>
		<PageHeader
			:data="{
				name: route,
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<div v-if="!error" class="row mb-3">
			<!-- Filters -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2"><font-awesome-icon :icon="['fas', 'filter']" class="text-primary me-1" /> Filters</h4>
								<button v-if="Object.values(this.params).length > 4 && searchActive === ''" @click="clearFilters()" class="btn btn-sm btn-danger text-white me-2">
									Clear
									<font-awesome-icon :icon="['fas', 'times']" />
								</button>
								<button v-if="!searchSaving && haveParamsToSave() && searchActive === ''" @click="searchSaving = true" class="btn btn-sm btn-primary">
									Save search
								</button>
								<div v-if="searchSaving && searchActive === ''" class="save-search d-flex">
									<form @submit.prevent="storeSearch" class="d-flex">
										<input type="text" v-model="searchName" class="form-control form-control-sm ms-2" placeholder="enter search name" required />
										<button type="submit" class="btn btn-sm btn-primary ms-1">
											Save
										</button>
										<a role="button" @click="searchSaving = false" class="btn btn-sm ms-1">
											Cancel
										</a>
									</form>
								</div>
								<span v-if="searchActive !== ''" class="badge bg-success"
									>{{ searchActive }} <font-awesome-icon role="button" @click="clearFilters()" class="ms-2" :icon="['fas', 'times']"
								/></span>
							</div>
							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasSearches"
									aria-controls="offcanvasSearches"
									class="btn btn-outline-secondary btn-sm me-2"
								>
									Saved Searches <font-awesome-icon :icon="['fas', 'save']" class="me-1" />
								</button>
							</div>
						</div>

						<LPDataTableFilters :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
					</div>
				</div>
			</div>
			<!-- Table data -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2 text-capitalize">{{ model }}</h4>
								<router-link
									v-if="authUser.permissions.includes(model.toUpperCase() + '_IMPORT') && false"
									:to="`/${route}/import`"
									class="btn btn-primary btn-sm me-2"
								>
									Import <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
								</router-link>
							</div>

							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasTableSettings"
									aria-controls="offcanvasTableSettings"
									class="btn btn-outline-secondary btn-sm"
								>
									Table Settings <font-awesome-icon :icon="['fas', 'cogs']" class="me-1" />
								</button>
							</div>
						</div>
						<table :class="!loading && tableData.length > 0 ? 'table table-hover' : 'table table-hover loading'">
							<LPDataTableThead :thead="table.columns" :userColumns="userColumns" v-on:setOrder="setOrderBy($event.orderBy, $event.direction)" />
							<LPDataTableTbody
								:userColumns="userColumns"
								:authUser="authUser"
								:data="tableData"
								:tbody="table.columns"
								:model="model"
								:route="route"
								:rowInfo="true"
								v-on:showInformationModal="showInformationModal($event)"
							/>
						</table>
						<div v-if="!loading && tableData.length === 0" class="col-md-12 text-center">
							<h4>No data available</h4>
						</div>
						<div v-if="loading && tableData.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div class="table-actions d-flex">
							<button data-bs-toggle="modal" data-bs-target="#exportModal" class="btn btn-success btn-sm me-2">
								Export <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
							</button>
							<router-link v-if="authUser.email.includes('srdjan')" :to="`/${route}/import`" class="btn btn-primary btn-sm me-2">
								Import <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
							</router-link>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
		</div>

		<div v-else class="row mb-3">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="alert alert-danger" role="alert">
							{{ error }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for displaying Data -->
		<div class="modal fade" id="dataInfoModal" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-body">
						<template v-if="!loadingDeliveryInfo">
							<img v-if="dataInfo" :src="dataInfo.data.thumbnails[0].large" width="100%" class="mb-2" />

							🔍
							<a v-if="dataInfo" class="fs-5" target="_blank" :href="dataInfo.data.url"> Preview PDF file</a>
						</template>
						<div v-else class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.campaign_name" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Campaign name</span> <span>{{ dataInfo.campaign_name }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.type" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Type</span> <span>{{ dataInfo.type }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.campaign_from" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Sender</span> <span>{{ dataInfo.campaign_from }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.carrier" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Carrier</span> <span>{{ dataInfo.carrier }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.lob_id" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">LOB id</span> <span>{{ dataInfo.lob_id }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.color" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Color</span> <span>{{ dataInfo.color }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.double_sided" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Double sided</span> <span>{{ dataInfo.double_sided }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.extra_service" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Extra service</span> <span>{{ dataInfo.extra_service }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.mail_type" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Mail type</span> <span>{{ dataInfo.mail_type }}</span>
							</div>
						</div>
						<div class="mt-2" v-if="dataInfo">
							<div v-if="dataInfo.send_at" class="d-flex justify-content-between align-items-center">
								<span class="fw-bold">Date</span> <span>{{ dataInfo.send_at }}</span>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for adding Data -->
		<div class="modal fade" id="create" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="create()">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">New user</h5>
							<small class="ms-2">User default role is user</small>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<h1>Create {{ model }}</h1>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<button type="submit" class="btn btn-primary">Save</button>
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for adding Filter -->
		<div class="modal fade" id="addFilter" tabindex="-1" aria-labelledby="addFilterLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="addFilter">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">Add Filter</h5>
							<small class="ms-2">Adding new filter for {{ this.model }}</small>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body text-center">
							<h1>Will come soon 😉</h1>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<!-- <button type="submit" class="btn btn-primary">Save</button> -->
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for export -->
		<div class="modal fade" id="exportModal" tabindex="-1" aria-labelledby="exportModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">Export {{ route }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="form-group mb-2">
							<label for="">Export Type</label>
							<select class="form-select" v-model="newExport.type">
								<option value="csv">CSV</option>
							</select>
						</div>
						<div class="form-group mb-2">
							<label for="">File Name</label>
							<div class="input-group mb-3">
								<input type="text" class="form-control" placeholder="File name" v-model="newExport.filename" />
								<span class="input-group-text" id="basic-addon2">.{{ newExport.type }}</span>
							</div>
						</div>
						<div class="form-group">
							<label for="">Columns to export</label>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="visible" id="visibleColumnsRadio" checked />
								<label class="form-check-label" for="visibleColumnsRadio">
									Export visible columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'visible'">
								<div class="d-flex flex-wrap">
									<div :key="'column-available-' + column" v-for="column in newExport.exportColumns" class="mb-2 me-1">
										<span class="badge bg-success">{{ column }}</span>
									</div>
								</div>
							</div>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="custom" id="customColumnsRadio" />
								<label class="form-check-label" for="customColumnsRadio">
									Export custom columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'custom'">
								<div class="d-flex flex-wrap">
									<small class="w-100 mb-1">ℹ️ Click on column to include/exclude it from export</small>
									<div :key="'column-available-' + column" v-for="column in newExport.availableColumns" class="mb-2 me-1">
										<span role="button" @click="toggleExportColumn(column)" v-if="newExport.exportColumns.includes(column)" class="badge bg-success">{{
											column
										}}</span>
										<span role="button" @click="toggleExportColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button v-if="!loadExporting && exportingFile === ''" @click="exportData()" type="submit" class="btn btn-primary">
							Create Export
						</button>
						<a v-if="!loadExporting && exportingFile !== ''" @click="downloadExport()" :href="exportingFile" class="btn btn-primary" download>
							Download
						</a>

						<button v-if="loadExporting" class="btn btn-primary" type="button" disabled>
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							Loading...
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Offcanvas for Filters -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasFilters" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Filters settings</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				Filters settings
			</div>
		</div>

		<!-- Offcanvas for Searches -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasSearches" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Saved searches</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Your saved searches</h4>
				<p class="p-0"><small>ℹ️ Click on search to apply search filters to table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'user-searches-' + search.id" v-for="search in searches" class="mb-2 me-1">
							<span
								data-bs-toggle="offcanvas"
								data-bs-target="#offcanvasSearches"
								aria-controls="offcanvasSearches"
								role="button"
								@click="applySearch(search)"
								class="badge bg-success"
								>{{ search.name }}</span
							>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Your saved searches for fast applying filters, you can add important ones to display above table (🚧 coming soon)</small>
			</div>
		</div>
		<!-- Offcanvas for Table Settings -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasTableSettings" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Table settings</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Display columns</h4>
				<p class="p-0"><small>ℹ️ Select which columns to display in table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'column-available-' + column" v-for="column in availableColumns" class="mb-2 me-1">
							<span role="button" @click="toggleUserColumn(column)" v-if="userColumns.includes(column)" class="badge bg-success">{{ column }}</span>
							<span role="button" @click="toggleUserColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Selected columns are automatically saved to your browser storage and will be used again when you come on this page</small>
				<h4 class="mt-3">Display {{ this.route }} per page</h4>
				<p class="p-0">
					<small>ℹ️ Select how many {{ this.route }} to display per page in table</small>
				</p>
				<div class="form-group w-75">
					<select v-model="userPerPage" @change="setUserPerPage()" class="form-select">
						<option value="10">10 per page</option>
						<option value="15">15 per page</option>
						<option value="20">20 per page</option>
						<option value="30">30 per page</option>
						<option value="50">50 per page</option>
						<option value="100">100 per page</option>
					</select>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import LPDataTableFilters from '@/components/lp-data-table/LPDataTableFilters'
import LPDataTableThead from '@/components/lp-data-table/LPDataTableThead'
import LPDataTableTbody from '@/components/lp-data-table/LPDataTableTbody'

export default {
	name: 'Deliveries',
	components: { PageHeader, LPDataTablePagination, LPDataTableFilters, LPDataTableThead, LPDataTableTbody },
	data() {
		return {
			route: 'deliveries',
			model: 'delivery',
			tableData: [],
			loading: false,
			error: '',
			newTableEntry: {},
			$createModal: null,
			$exportModal: null,
			$addFilterModal: null,
			pagination: {},
			page: this.$route.query.page || 1,
			orderBy: this.$route.query.orderBy || 'send_at',
			direction: this.$route.query.direction || 'desc',
			userPerPage: localStorage.getItem('delivery-per-page') || 20,
			table: {
				columns: [
					{
						name: 'id',
						label: 'ID',
						sort: true,
						type: 'default',
					},
					{
						name: 'lob_id',
						label: 'LOB Id',
						sort: false,
						type: 'default',
					},
					{
						name: 'type',
						label: 'Type',
						sort: true,
						type: 'default',
					},
					{
						name: 'municipality_id',
						label: 'Municipality',
						sort: true,
						type: 'default',
					},
					{
						name: 'fips',
						label: 'FIPS',
						sort: true,
						type: 'default',
					},
					{
						name: 'population',
						label: 'Population',
						sort: true,
						type: 'default',
					},
					{
						name: 'campaign_id',
						label: 'Campaign ID',
						sort: false,
						type: 'default',
					},
					{
						name: 'campaign_name',
						label: 'Campaign Name',
						sort: false,
						type: 'default',
					},
					{
						name: 'campaign_from',
						label: 'From',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_id',
						label: 'TO ID',
						sort: true,
						type: 'default',
					},
					{
						name: 'to_name',
						label: 'Name',
						sort: true,
						type: 'default',
					},
					{
						name: 'to_company',
						label: 'Company',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_phone',
						label: 'Phone',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_email',
						label: 'Email',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_address_line1',
						label: 'Address Line 1',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_address_line2',
						label: 'Address Line 2',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_address_city',
						label: 'City',
						sort: false,
						type: 'default',
					},
					{
						name: 'to_address_state',
						label: 'State',
						sort: true,
						type: 'default',
					},
					{
						name: 'to_address_zip',
						label: 'ZIP',
						sort: true,
						type: 'default',
					},
					{
						name: 'to_address_country',
						label: 'Country',
						sort: true,
						type: 'default',
					},
					{
						name: 'from_id',
						label: 'From ID',
						sort: true,
						type: 'default',
					},
					{
						name: 'size',
						label: 'Size',
						sort: true,
						type: 'default',
					},
					{
						name: 'color',
						label: 'Color',
						sort: true,
						type: 'boolean',
						yes: 'Yes',
						no: 'No',
					},
					{
						name: 'double_sided',
						label: 'Double sided',
						sort: true,
						type: 'boolean',
						yes: 'Yes',
						no: 'No',
					},
					{
						name: 'address_placement',
						label: 'Address placement',
						sort: true,
						type: 'default',
					},
					{
						name: 'extra_service',
						label: 'Extra service',
						sort: true,
						type: 'default',
					},
					{
						name: 'mail_type',
						label: 'Mail Type',
						sort: true,
						type: 'default',
					},
					{
						name: 'carrier',
						label: 'Carrier',
						sort: false,
						type: 'default',
					},
					{
						name: 'tracking_events',
						label: 'Tracking Events',
						sort: false,
						type: 'default',
					},
					{
						name: 'matching',
						label: 'Matching %',
						sort: true,
						type: 'default',
					},
					{
						name: 'message',
						label: 'Message',
						sort: true,
						type: 'default',
					},
					{
						name: 'status',
						label: 'Status',
						sort: true,
						type: 'default',
					},
					{
						name: 'expected_delivery_at',
						label: 'Expected Delivery',
						sort: false,
						type: 'default',
					},
					{
						name: 'send_at',
						label: 'Send',
						sort: true,
						type: 'default',
					},
					{
						name: 'created_at',
						label: 'Created',
						sort: false,
						type: 'default',
					},
					{
						name: 'updated_at',
						label: 'Updated',
						sort: false,
						type: 'default',
					},
					{
						name: 'actions',
						label: 'Actions',
						sort: false,
						type: 'actions',
						width: 80,
						buttons: [
							{
								type: 'default',
								label: 'View',
								action: '',
								class: 'btn-secondary',
								permission: `DELIVERY_ACCESS`,
							},
						],
					},
				],
				filters: [
					{
						display: true,
						name: 'search',
						label: 'Search',
						type: 'text',
						placeholder: 'enter text and press enter',
						colClass: 'col-3',
						value: this.$route.query.search || '',
					},
					{
						display: true,
						name: 'campaign_name',
						label: 'Campaign',
						type: 'select',
						placeholder: 'All campaigns',
						colClass: 'col-3',
						options: [],
						optionValue: false,
						optionsEndpoint: `deliveries/names`,
						value: this.$route.query.campaign_name || [],
					},
					{
						display: true,
						name: 'type',
						label: 'Type',
						type: 'boolean',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.type || '',
						options: [
							{
								id: 'Postcard',
								name: 'Postcard',
							},
							{
								id: 'Letter',
								name: 'Letter',
							},
						],
					},
					{
						display: true,
						name: 'population',
						label: 'Population',
						type: 'range',
						placeholder: '',
						colClass: 'col-3',
						min: 0,
						max: 99999999,
						from: this.$route.query.frompopulation || '',
						to: this.$route.query.topopulation || '',
					},
					{
						display: true,
						name: 'send_at',
						label: 'Date',
						type: 'daterange',
						placeholder: '',
						colClass: 'col-4',
						from: this.$route.query.fromsend_at || '',
						to: this.$route.query.tosend_at || '',
					},
				],
			},
			params: {},
			userColumns: JSON.parse(localStorage.getItem(`delivery-columns`)) || ['id', 'type', 'actions'],
			availableColumns: ['id'],
			searchName: '',
			searchSaving: false,

			searchActive: '',
			searches: [],
			newExport: {
				type: 'csv',
				columnsType: 'visible',
				filename: '',
				availableColumns: ['id'],
				exportColumns: JSON.parse(localStorage.getItem(`delivery-columns`)) || ['id', 'type', 'actions'],
			},
			loadExporting: false,
			exportingFile: '',
			$dataInfoModal: null,
			dataInfo: null,
			loadingDeliveryInfo: false,
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.table.filters.forEach(filter => {
			if (filter.value && filter.value !== '' && filter.value.length > 0) {
				this.params[filter.name] = filter.value
			} else {
				delete this.params[filter.name]
			}

			if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
				this.params['from' + filter.name] = filter.from
				this.params['to' + filter.name] = filter.to
			} else {
				delete this.params['from' + filter.name]
				delete this.params['to' + filter.name]
			}
		})
		this.getTableColumns()
		this.getTableData(true)
		this.getSearches()
	},
	mounted() {
		this.$createModal = new Modal('#create')
		this.$addFilterModal = new Modal('#addFilter')
		this.$exportModal = new Modal('#exportModal')
		this.$dataInfoModal = new Modal('#dataInfoModal')
	},
	methods: {
		// Get table Columns
		getTableColumns() {
			restApi
				.post('/helper/table-columns', { table: this.route })
				.then(({ data }) => {
					this.newExport.availableColumns = data.map(column => column.name)
					this.availableColumns = data.map(column => column.name)
					this.availableColumns.push('actions')
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		// Get table data from API
		getTableData(first = false) {
			this.loading = true

			this.params = {
				...this.params,
				page: this.page,
				orderBy: this.orderBy,
				direction: this.direction,
				perPage: localStorage.getItem(`${this.model}-per-page`) || 20,
			}

			restApi
				.get(`/${this.route}`, { params: this.params })
				.then(({ data }) => {
					this.tableData = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					if (!first) {
						this.$router.push({ path: this.$router.currentPath, query: this.params })
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Adding new filter
		addFilter() {
			this.$addFilterModal.hide()
		},

		// Go to user page
		viewUser(user) {
			this.$router.push('/users/' + user.id)
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getTableData()
		},

		// Set direction
		setOrderBy(orderBy, direction) {
			this.orderBy = orderBy
			this.direction = direction
			this.getTableData()
		},

		// Get filters from LPDataTableFilters component
		getFilters(filters) {
			this.page = ''
			this.searchActive = ''
			filters.forEach(filter => {
				if (filter.value && filter.value !== '' && filter.value.length > 0) {
					this.params[filter.name] = filter.value
				} else {
					delete this.params[filter.name]
				}

				// Setting filter if its type is range
				if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
					this.params['from' + filter.name] = filter.from
					this.params['to' + filter.name] = filter.to
				} else {
					delete this.params['from' + filter.name]
					delete this.params['to' + filter.name]
				}
			})
			this.getTableData()
		},

		// Toggle user column
		toggleUserColumn(column) {
			if (this.userColumns.includes(column)) {
				this.userColumns = this.userColumns.filter(c => c !== column)
			} else {
				this.userColumns.push(column)
			}
			this.updateUserColumns()
		},

		// Update user columns
		updateUserColumns() {
			localStorage.setItem(`${this.model}-columns`, JSON.stringify(this.userColumns))
		},

		// Get user saved searches
		getSearches() {
			let params = {
				user_id: this.authUser.id,
				model: this.model,
			}
			restApi
				.get('/searches', { params })
				.then(({ data }) => {
					data.forEach(d => {
						d.data = JSON.parse(d.data)
						return d
					})
					this.searches = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Enable saving if some filters are set
		haveParamsToSave() {
			if (Object.values(this.params).length > 4) {
				return true
			} else {
				return false
			}
		},

		// Get user saved searches
		storeSearch() {
			let dataParams = this.params
			restApi
				.post('/searches', {
					user_id: this.authUser.id,
					model: this.model,
					name: this.searchName,
					data: JSON.stringify(dataParams),
				})
				.then(({ data }) => {
					data.data = JSON.parse(data.data)
					this.searches.push(data)
					this.searchActive = this.searchName
					this.searchSaving = false
					this.$swal.fire({
						icon: 'success',
						title: 'Search successfully saved!',
						timer: 4000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Apply saved search to table
		applySearch(search) {
			// Reset all before applying saved search
			this.params = {}
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'daterange') {
					filter.value = ''
				} else {
					filter.from = ''
					filter.to = ''
				}
			})
			// Apply saved search
			this.params = search.data
			let paramsArray = Object.keys(search.data)
			this.table.filters.forEach(filter => {
				if (paramsArray.includes(filter.name) && filter.type !== 'range') {
					filter.value = this.params[filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'range') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'daterange') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
			})
			this.searchActive = search.name
			this.getTableData()
		},

		// Clear all filters
		clearFilters() {
			this.params = {}
			this.searchActive = ''
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'multiselect') {
					filter.value = ''
				}
				if (filter.type === 'multiselect') {
					filter.value = []
				}
				if (filter.type === 'range' || filter.type === 'daterange') {
					filter.from = ''
					filter.to = ''
				}
			})
			this.getTableData()
		},

		// Toggle export column
		toggleExportColumn(column) {
			if (this.newExport.exportColumns.includes(column)) {
				this.newExport.exportColumns = this.newExport.exportColumns.filter(c => c !== column)
			} else {
				this.newExport.exportColumns.push(column)
			}
		},

		// Export table Data
		exportData() {
			this.loadExporting = true
			this.newExport.exportColumns = this.newExport.exportColumns.filter(column => column !== 'actions')
			restApi
				.post(`/${this.route}/export`, {
					params: JSON.stringify(this.params),
					columns: JSON.stringify(this.newExport.exportColumns),
					filename: this.newExport.filename,
				})
				.then(({ data }) => {
					this.loadExporting = false
					this.exportingFile = this.storageUrl + '/tmp/' + data.file
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Download exported Data
		downloadExport() {
			this.$exportModal.hide()
			this.newExport.filename = ''
			this.exportingFile = ''
		},

		// Set user per page
		setUserPerPage() {
			localStorage.setItem(`${this.model}-per-page`, this.userPerPage)
			this.getTableData()
		},

		showInformationModal(event) {
			restApi
				.get(`/${event.route}/${event.id}`)
				.then(({ data }) => {
					this.dataInfo = data
					this.$dataInfoModal.show()
					this.getDeliveryInfo(data.type, data.lob_id)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},

		getDeliveryInfo(type, lob_id) {
			this.loadingDeliveryInfo = true
			restApi
				.post(`/deliveries/info`, { type: type, lob_id: lob_id })
				.then(({ data }) => {
					this.dataInfo.data = data
					this.loadingDeliveryInfo = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},
	},
}
</script>
